// Guard de ruta reutilizable para features de pago (add-ons / módulos).
//
// Problema (F10): el gate de un add-on de pago vivía duplicado dentro de cada página
// (un useEffect que lee `hasModule(X)` y hace navigate). Si el dev olvida replicarlo,
// la feature queda accesible por URL directa aunque el comercio no tenga el módulo
// contratado (ver F8/F9). Este componente centraliza el gate a nivel de ruta: se envuelve
// el `element` de la ruta y se declara el módulo requerido en un solo lugar.
//
// Comportamiento:
//   - mientras los módulos cargan (`isModulesLoading`, `hasModule` devuelve null) → LoadingPage.
//     Evita el falso negativo de redirigir antes de saber qué módulos tiene el comercio.
//   - módulos cargados y SIN el módulo → redirect a `redirectTo` (default /menu/productos).
//   - con el módulo → renderiza los hijos.
//
// Migración: el resto de features de pago deberían migrar a este guard en vez de duplicar
// el gate in-page. Las páginas pueden conservar su guard in-page como defensa redundante
// (no rompe), pero la ruta envuelta es la fuente de verdad del acceso.

import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import useModules from '@utils/hooks/useModules';
import type { ModuleKey } from '@interfaces/Permissions';
import LoadingPage from '@pages/LoadingPage';

interface ProtectedModuleRouteProps {
  module: ModuleKey;
  children: ReactNode;
  /** Ruta a la que redirigir cuando el comercio no tiene el módulo. */
  redirectTo?: string;
}

const ProtectedModuleRoute = ({ module, children, redirectTo = '/menu/productos' }: ProtectedModuleRouteProps) => {
  const { hasModule, isModulesLoading } = useModules();

  // Aún no sabemos qué módulos tiene el comercio: no decidir todavía (evita redirigir
  // por un falso negativo durante la carga).
  if (isModulesLoading) {
    return <LoadingPage />;
  }

  // Denegar solo cuando sabemos con certeza que el comercio NO tiene el módulo.
  // `hasModule` devuelve `null` cuando aún no hay módulos cargados: en ese caso no
  // redirigir (evita el falso negativo). El chequeo estricto `=== false` distingue
  // "no tiene el módulo" de "todavía no se sabe".
  if (hasModule(module) === false) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default ProtectedModuleRoute;
